import { Divider, Flex, Group } from '@byecode/ui'
import {
    type FlowNode,
    getAllCanBeUpstreamNodes,
    getInputInnerTypeMatch,
    getUpstreamNodesOptions,
    ListItem4ByecodeUi,
    NodeFieldPreview,
    type NodeTypes,
    OtherPlatformNodeType,
    OUTPUT_SINGLE_DATA_NODES,
    useAtomData,
    type WeChatProfitSharingQueryActionConfig
} from '@lighthouse/shared'
import React, { useMemo } from 'react'

import { workflowListAtom } from '@/atoms/workflow/state'
import { PopoverQuestion } from '@/components/PopoverQuestion'
import { WechatPaymentAccountSelect } from '@/components/WechatPaymentAccountSelect'
import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import { useDataSourceList } from '@/hooks/useDataSource'
import { useIsDisabledWithVersion } from '@/hooks/useIsDisabledWithVersion'
import { useGetUserRecordOptions } from '@/hooks/usePage'

import OutputVariable from '../../../OutputVariable'
import * as CM from '../../styles'

interface WeChatProfitSharingQueryActionConfigureProps {
    nodeType?: NodeTypes
    allParentNodes: FlowNode[]
    actionTypeSwitcher?: React.ReactNode
    value: WeChatProfitSharingQueryActionConfig
    onChange: (value: WeChatProfitSharingQueryActionConfig) => void
}

export const WeChatProfitSharingQueryActionConfigure: React.FC<WeChatProfitSharingQueryActionConfigureProps> = ({
    allParentNodes,
    actionTypeSwitcher,
    value,
    onChange
}) => {
    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const dataSourceList = useDataSourceList(appId, envId)
    const disabledWithVersion = useIsDisabledWithVersion()
    const workflowList = useAtomData(workflowListAtom)

    const textUserOption = useGetUserRecordOptions()
    const singleRecordNodes = useMemo(() => {
        return allParentNodes.filter(node => OUTPUT_SINGLE_DATA_NODES.has(node.data.nodeType))
    }, [allParentNodes])
    const textUpstreamOptions = useMemo(
        () =>
            getUpstreamNodesOptions({
                workflowList,
                dataSourceList,
                upstreamNodes: singleRecordNodes,
                allUpstreamNodes: allParentNodes
            }),
        [allParentNodes, dataSourceList, singleRecordNodes, workflowList]
    )

    const header = useMemo(() => {
        if (!actionTypeSwitcher) {
            return null
        }
        return (
            <>
                <Group label="动作配置">
                    <ListItem4ByecodeUi>
                        <CM.FormItemLabelWrapper>
                            <CM.FormItemLabel>类型</CM.FormItemLabel>
                        </CM.FormItemLabelWrapper>
                        <CM.FormItemContent>{actionTypeSwitcher}</CM.FormItemContent>
                    </ListItem4ByecodeUi>
                </Group>
                <Divider />
            </>
        )
    }, [actionTypeSwitcher])

    return (
        <CM.Container>
            {header}
            <WechatPaymentAccountSelect
                value={value?.paymentAccountId}
                onChange={paymentAccountId => onChange({ ...value, paymentAccountId })}
            />
            <Divider />
            <Group label="分账单号">
                <CM.FieldList>
                    <CM.FieldItem>
                        <PopoverQuestion
                            type="profitSharingOrderId"
                            renderTarget={(node, ref) => (
                                <Flex gap={16} ref={ref} alignItems="center">
                                    <CM.FieldTitle required>商户分账单号</CM.FieldTitle>
                                    {node}
                                </Flex>
                            )}
                        />
                        <NodeFieldPreview
                            innerTypes={getInputInnerTypeMatch('TEXT')}
                            disabled={disabledWithVersion}
                            value={value.profitSharingOrderId}
                            onBlur={profitSharingOrderId => onChange({ ...value, profitSharingOrderId })}
                            autoHeight
                            userOption={textUserOption}
                            upstreamOptions={textUpstreamOptions}
                            dataSourceList={dataSourceList}
                        />
                    </CM.FieldItem>
                    <CM.FieldItem>
                        <PopoverQuestion
                            type="outTradeNo"
                            renderTarget={(node, ref) => (
                                <Flex gap={16} ref={ref} alignItems="center">
                                    <CM.FieldTitle required>微信订单号</CM.FieldTitle>
                                    {node}
                                </Flex>
                            )}
                        />
                        <NodeFieldPreview
                            innerTypes={getInputInnerTypeMatch('TEXT')}
                            disabled={disabledWithVersion}
                            value={value.outTradeNo}
                            onBlur={outTradeNo => onChange({ ...value, outTradeNo })}
                            autoHeight
                            userOption={textUserOption}
                            upstreamOptions={textUpstreamOptions}
                            dataSourceList={dataSourceList}
                        />
                    </CM.FieldItem>
                </CM.FieldList>
            </Group>
            <OutputVariable nodeType={OtherPlatformNodeType.WECHAT_PROFIT_SHARING_QUERY} />
        </CM.Container>
    )
}
